import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  userProfileService,
  UserProfile
} from '../services/userProfileService';
import axios from 'axios';

// Types
interface User {
  username: string;
  displayName?: string;
  role: string; // For backward compatibility
  roles: string[]; // Array of user roles
  companyId: string;
  userProfile?: UserProfile; // Add user profile data
}

interface AuthContextProps {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: () => boolean;
  updateUserDisplayName: (newName: string) => void; // Simple method to update display name
  updateUserProfile: (profile: UserProfile) => void; // Method to update user profile
}

// Create the context with a default value
// Using named export for better HMR compatibility
export const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: false,
  login: async () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  logout: () => {},
  isAuthenticated: () => false,
  updateUserDisplayName: () => {
    // This is intentionally empty in the default context and will be implemented by the provider
  },
  updateUserProfile: () => {
    // This is intentionally empty in the default context and will be implemented by the provider
  }
});

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Export the provider component as a named export for HMR compatibility
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    const token = localStorage.getItem('token');

    if (!token || !currentUser) {
      clearAuthData();
    } else {
      setUser(currentUser);

      // Fetch profile data on mount if user is authenticated
      if (currentUser && !currentUser.userProfile) {
        fetchUserProfile(currentUser);
      }
    }
    setLoading(false);
  }, []);

  const clearAuthData = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedTenant');
    // Add any other auth-related items that need to be cleared
  };

  // Fetch user profile data
  const fetchUserProfile = async (currentUser: User) => {
    try {
      const profileData = await userProfileService.getUserProfile();

      if (profileData) {
        const updatedUser = {
          ...currentUser,
          displayName: profileData.name,
          userProfile: profileData
        };

        setUser(updatedUser);

        // Update localStorage to maintain consistency
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('AuthProvider: Error fetching user profile:', error);
    }
  };

  // Method to update user profile in context
  const updateUserProfile = (profile: UserProfile) => {
    if (user) {
      const updatedUser = {
        ...user,
        displayName: profile.name,
        userProfile: profile
      };

      setUser(updatedUser);

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Simple method to update display name in AuthContext
  const updateUserDisplayName = (newName: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        displayName: newName
      };

      setUser(updatedUser);

      // Also update localStorage to maintain consistency
      const existingUserData = localStorage.getItem('user');
      let parsedUserData = {};

      try {
        parsedUserData = existingUserData ? JSON.parse(existingUserData) : {};
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
        parsedUserData = {};
      }

      const updatedUserData = {
        ...parsedUserData,
        ...updatedUser
      };

      localStorage.setItem('user', JSON.stringify(updatedUserData));
    } else {
      console.warn(
        'AuthProvider: Attempted to update display name but no user is logged in'
      );
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const newAuthData = await authService.login(email, password);

      if (newAuthData) {
        setUser(newAuthData);

        // Fetch user profile data after successful login
        await fetchUserProfile(newAuthData);

        return true;
      } else {
        console.error('AuthProvider: Login failed - no auth data returned');
        return false;
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return false;
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Reset login attempt tracking flag
    (window as any).__hasAttemptedLogin = false;
    // Clear all auth data
    clearAuthData();
    authService.logout();
    navigate('/login', { replace: true });
  };

  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    // Only consider authenticated if we have both a user in context and a valid token
    const isAuth = !!user && !!token;
    return isAuth;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated,
        updateUserDisplayName,
        updateUserProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
